import { LoaderFunctionArgs, json } from '@remix-run/node'
import { prisma } from '#app/utils/db.server.ts'
import {
	MetaFunction,
	redirect,
	useLoaderData,
	useParams,
	useSearchParams,
} from '@remix-run/react'
import { invariant } from '@epic-web/invariant'
import { Icon } from '#app/components/ui/icon.tsx'
import { format } from 'date-fns'
import { Badge } from '#app/components/ui/badge.tsx'
import { ClientOnly } from 'remix-utils/client-only'
import DispensaryMap from '../components/dispensary-map'
import { Link } from '@remix-run/react'
import { stripHtml } from '#app/utils/misc.js'

interface ProductParent {
	id: number
	name: string
	coming_soon: boolean
	listing_state: string
}

export const meta: MetaFunction<typeof loader> = ({
	data,
	matches,
	location,
}) => {
	const rootData = matches.find((match) => match.id === 'root')?.data as {
		generalSettings?: {
			website_name: string
			twitter_site: string
			twitter_creator: string
		}
		requestInfo?: { origin: string }
	}

	// Fallback chain for title and description
	const title =
		data?.customer?.leafCustomerMetadata?.title || // First choice: SEO title
		data?.customer?.name || // Second choice: display name
		'Dispensary' // Final fallback

	const description =
		data?.customer?.leafCustomerMetadata?.description || // First choice: SEO description
		stripHtml(data?.customer?.description) || // Second choice: product description
		'Dispensary description' // Final fallback

	const fullUrl = location.search
		? `${rootData?.requestInfo?.origin}${location.pathname + location.search}`
		: `${rootData?.requestInfo?.origin}${location.pathname}`

	return [
		{ title: `${title} | ${rootData?.generalSettings?.website_name}` },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: fullUrl },
		{ property: 'og:type', content: 'product' },
		{ property: 'twitter:card', content: 'summary_large_image' },
		{ property: 'twitter:title', content: title },
		{ property: 'twitter:description', content: description },

		// Conditional Twitter tags
		...(rootData?.generalSettings?.twitter_site
			? [
					{
						name: 'twitter:site',
						content: rootData.generalSettings.twitter_site,
					},
				]
			: []),
		...(rootData?.generalSettings?.twitter_creator
			? [
					{
						name: 'twitter:creator',
						content: rootData.generalSettings.twitter_creator,
					},
				]
			: []),
	]
}

export async function loader({ request, params }: LoaderFunctionArgs) {
	const { slug, location } = params
	const url = new URL(request.url)
	invariant(slug, 'Missing Dispensary ID')

	//console.log('params', params)

	// Check number of companies
	const companyCount = await prisma.leafCompany.count()

	// If multiple companies exist but no location specified, redirect to state selection
	// ELSE if single company with location specified, redirect to canonical URL
	if (companyCount > 1 && !location) {
		// Multiple companies but no location - redirect to state selection
		return redirect(`/`)
		// TODO:
		// return redirect(`/select-location?redirect=/products/${slug}${url.search}`)
	} else if (companyCount === 1 && location) {
		// Single company with location - redirect to canonical URL
		return redirect(`/dispensaries/${slug}${url.search}`)
	}

	const customer = await prisma.leafCustomer.findFirst({
		where: {
			OR: [
				{ leafCustomerMetadata: { slug: params.slug, show_on_website: true } },
				{ internalId: params.slug },
			],
		},
		select: {
			id: true,
			internalId: true,
			name: true,
			description: true,
			phone: true,
			website: true,
			delivery_address: true,
			address: true,
			city: true,
			state: true,
			zipcode: true,
			lat: true,
			long: true,
			orders: {
				take: 1,
				where: {
					status: { in: ['Fulfilled', 'Shipped'] },
					//status: 'Complete',
					total: {
						path: ['amount'],
						gt: 1,
					},
				},
				orderBy: [{ created_on: 'desc' }],
				select: {
					internalId: true,
					created_on: true,
					status: true,
					lineItems: {
						select: {
							internalId: true,
							quantity: true,
							leafProduct: {
								where: {
									leafProductMetadata: {
										show_on_website: true,
									},
								},
								select: {
									internalId: true,
									id: true,
									name: true,
									display_name: true,
									images: true,
									parent: true,
									leafProductCategory: {
										select: { name: true, slug: true },
									},
									leafProductSubcategory: {
										select: { name: true, slug: true },
									},
									leafProductMetadata: {
										select: {
											slug: true,
											title: true,
											description: true,
											show_on_website: true,
										},
										where: {
											show_on_website: true,
										},
									},
								},
							},
						},
					},
				},
			},
			leafCustomerMetadata: {
				select: {
					slug: true,
					title: true,
					description: true,
					latitude: true,
					longitude: true,
				},
			},
		},
	})

	invariant(customer, 'Dispensary not found')

	// Extract parent IDs from the products
	const parentIds = customer.orders[0]?.lineItems
		.map((item) => (item.leafProduct?.parent as ProductParent)?.id)
		.filter((id): id is number => id !== undefined)

	// If we have any products with parents, fetch their images
	if (parentIds && parentIds.length > 0) {
		const parentImages = await prisma.leafProduct.findMany({
			where: {
				id: {
					in: parentIds,
				},
			},
			select: {
				id: true,
				images: {
					select: {
						id: true,
						image: true,
					},
				},
			},
		})

		// Add parent images to products
		customer.orders[0]?.lineItems.forEach((item) => {
			if (
				item.leafProduct?.parent &&
				(item.leafProduct.parent as ProductParent).id
			) {
				const parentImage = parentImages.find(
					(p) => p.id === (item.leafProduct?.parent as ProductParent).id,
				)
				if (parentImage) {
					;(
						item.leafProduct as typeof item.leafProduct & {
							parentImages: typeof parentImage.images
						}
					).parentImages = parentImage.images
				}
			}
		})
	}

	// Calculate category counts from the most recent order
	const categoryCounts = customer.orders[0]?.lineItems
		.filter((item) => item.leafProduct !== null)
		.reduce<Record<string, number>>((acc, item) => {
			const category = item.leafProduct?.leafProductCategory?.slug
			if (category) {
				acc[category] = (acc[category] || 0) + 1
			}
			return acc
		}, {})

	return json({ customer, categoryCounts })
}

export default function Dispensary() {
	const { customer, categoryCounts } = useLoaderData<typeof loader>()
	const { location } = useParams()

	//console.log(customer)

	//console.log(categoryCounts)

	// Format the address
	const fullAddress = [
		customer.address,
		customer.city,
		customer.state,
		customer.zipcode,
	]
		.filter(Boolean)
		.join(', ')

	return (
		<>
			<main className="relative">
				<div className="relative z-[1] overflow-hidden bg-theme-dark pb-20 pt-28 text-white lg:py-32">
					<div className="container grid grid-cols-6 items-center gap-6">
						<div className="col-span-6 space-y-1 md:col-span-4">
							<h1 className="mb-3 text-balance text-3xl font-extrabold md:text-6xl">
								{customer.name}
							</h1>
							{fullAddress && (
								<p className="mb-7 flex capitalize last:mb-0">
									<Icon name="sewing-pin" className="mr-1" />
									{fullAddress}
								</p>
							)}
						</div>
						{Object.keys(categoryCounts ?? {}).length > 0 && (
							<div className="col-span-6 md:col-span-2">
								<div className="rounded-lg border-4 border-theme-blue bg-theme-dark p-4">
									<div className="text-sm">
										<div className="grid gap-3">
											<div className="flex justify-between font-semibold">
												<h2>Recent Order</h2>
												{customer.orders?.[0]?.created_on && (
													<span className="font-normal">
														{format(
															new Date(customer.orders[0].created_on),
															'MMM d, yyyy',
														)}
													</span>
												)}
											</div>
											<ul className="grid gap-3">
												{categoryCounts &&
													Object.entries(categoryCounts).map(
														([category, count]) => (
															<li
																key={category}
																className="flex items-center justify-between"
															>
																<span className="capitalize text-white">
																	{/* Optional: handle missing category names */}
																	{category || 'Uncategorized'}
																</span>
																<span>{count}</span>
															</li>
														),
													)}
												<li className="flex items-center justify-between font-semibold">
													<span className="text-white">Total</span>
													<span>
														{
															customer.orders?.[0]?.lineItems.filter(
																(item) =>
																	item.leafProduct?.leafProductMetadata
																		?.show_on_website,
															).length
														}
													</span>
												</li>
											</ul>
										</div>
									</div>
								</div>
							</div>
						)}
					</div>

					<div
						aria-hidden="true"
						className="absolute bottom-10 left-[calc(50%-5rem)] -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:bottom-[calc(50%-30rem)] lg:left-48 xl:left-[calc(50%-24rem)]"
					>
						<div
							style={{
								clipPath:
									'polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)',
							}}
							className="aspect-[1108/632] w-[69.25rem] bg-gradient-to-r from-[#80caff] to-[#4f46e5] opacity-20"
						/>
					</div>

					<div className="absolute -left-[150px] bottom-[70px] -z-[1] h-[300px] w-[300px] animate-spin rounded-[500px] bg-blue-purple-gradient-1 opacity-20 blur-3xl"></div>
					<div className="absolute bottom-[20px] left-[57px] -z-[1] h-[250px] w-[250px] animate-spin rounded-[450px] bg-blue-purple-gradient-2 opacity-20 blur-3xl"></div>
				</div>

				<div className="container py-16">
					<div className="mb-20">
						<div className="rounded-lg border shadow-sm">
							<div className="relative w-full overflow-auto">
								<table className="w-full caption-bottom text-sm">
									<thead className="[&_tr]:border-b">
										<tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
											<th className="hidden h-12 w-[100px] px-4 text-left align-middle font-medium text-muted-foreground sm:table-cell">
												<span className="sr-only">Image</span>
											</th>
											<th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
												Name
											</th>
											<th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
												Category
											</th>
											<th className="hidden h-12 px-4 text-left align-middle font-medium text-muted-foreground md:table-cell">
												Subcategory
											</th>
											<th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
												<span className="sr-only">Actions</span>
											</th>
										</tr>
									</thead>
									<tbody className="[&_tr:last-child]:border-0">
										{customer.orders?.[0]?.lineItems
											.filter((item) => item.leafProduct !== null)
											.map((item) => {
												const product = item.leafProduct
												const hasOwnImages =
													product?.images && product.images.length >= 1
												const hasParentImages =
													(product as any).parentImages?.length >= 1
												const imageToUse = hasOwnImages
													? product.images[0]?.image
													: hasParentImages
														? (product as any).parentImages[0]?.image
														: null

												return (
													<tr
														key={item.internalId}
														className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
													>
														<td className="hidden p-4 sm:table-cell">
															{imageToUse ? (
																<img
																	src={imageToUse}
																	alt={product?.display_name ?? ''}
																	className="aspect-square rounded-md object-cover"
																	height="64"
																	width="64"
																/>
															) : (
																<div className="flex aspect-square items-center justify-center rounded-md bg-gray-100">
																	<Icon
																		name="image"
																		className="h-8 w-8 text-gray-400"
																	/>
																</div>
															)}
														</td>
														<td className="p-4 font-medium">
															{product?.display_name}
														</td>
														<td className="p-4">
															<Badge variant="outline">
																{product?.leafProductCategory?.name}
															</Badge>
														</td>
														<td className="hidden p-4 md:table-cell">
															<Badge variant="outline">
																{product?.leafProductSubcategory?.name}
															</Badge>
														</td>
														<td className="p-4">
															<Link
																to={`${location ? `/${location}` : ''}/products/${product?.leafProductMetadata?.slug ?? product?.internalId}`}
																className="inline-flex items-center justify-center text-sm font-medium"
															>
																<Icon name="eye-open" />
																<span className="sr-only">
																	View {product?.name}
																</span>
															</Link>
														</td>
													</tr>
												)
											})}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</main>
			<ClientOnly fallback={<div className="h-[500px] bg-gray-100" />}>
				{() => <DispensaryMap dispensaries={[{ leafCustomer: customer }]} />}
			</ClientOnly>
		</>
	)
}
